import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertTriangle, XCircle, Info, Bell, Building2 } from 'lucide-react';

interface SystemAlert {
  id: string;
  type: 'error' | 'warning' | 'info';
  title: string;
  description: string;
  tenantName?: string;
  inboxName?: string;
  createdAt: string;
}

interface SystemAlertsCardProps {
  data: SystemAlert[];
  isLoading?: boolean;
}

function getAlertIcon(type: string) {
  switch (type) {
    case 'error': return XCircle;
    case 'warning': return AlertTriangle;
    default: return Info;
  }
}

function getAlertStyles(type: string): { iconColor: string; bgColor: string; borderColor: string } {
  switch (type) {
    case 'error':
      return {
        iconColor: 'text-red-500',
        bgColor: 'bg-red-500/5',
        borderColor: 'border-red-500/20',
      };
    case 'warning':
      return {
        iconColor: 'text-yellow-500',
        bgColor: 'bg-yellow-500/5',
        borderColor: 'border-yellow-500/20',
      };
    default:
      return {
        iconColor: 'text-blue-500',
        bgColor: 'bg-blue-500/5',
        borderColor: 'border-blue-500/20',
      };
  }
}

export function SystemAlertsCard({ data, isLoading }: SystemAlertsCardProps) {
  const errorCount = data.filter(a => a.type === 'error').length;
  const warningCount = data.filter(a => a.type === 'warning').length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Alertas do Sistema
            </CardTitle>
            <CardDescription>Problemas que precisam de atenção</CardDescription>
          </div>
          {!isLoading && (errorCount > 0 || warningCount > 0) && (
            <div className="flex gap-2">
              {errorCount > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/10 text-red-500 text-xs font-medium">
                  <XCircle className="h-3 w-3" />
                  {errorCount}
                </div>
              )}
              {warningCount > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-500 text-xs font-medium">
                  <AlertTriangle className="h-3 w-3" />
                  {warningCount}
                </div>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg border">
                <Skeleton className="h-5 w-5 rounded-full shrink-0" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-32 mb-2" />
                  <Skeleton className="h-3 w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <ScrollArea className="h-[300px] pr-4">
            <div className="space-y-3">
              {data.map((alert) => {
                const Icon = getAlertIcon(alert.type);
                const styles = getAlertStyles(alert.type);
                
                return (
                  <div 
                    key={alert.id} 
                    className={`flex items-start gap-3 p-3 rounded-lg border ${styles.bgColor} ${styles.borderColor}`}
                  >
                    <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${styles.iconColor}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{alert.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">{alert.description}</p>
                      {alert.tenantName && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                          <Building2 className="h-3 w-3" />
                          <span>{alert.tenantName}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
